/**
 * Camada CONTATO do painel de detalhes da conversa.
 *
 * É a camada que responde à queixa "falta informação do contato": canais,
 * nascimento e endereço, que hoje existem no cadastro mas nunca chegavam à
 * tela do atendimento.
 *
 * Só o miolo — a moldura (voltar, título, rolagem) é do `DetailsLayer`.
 */
import React, { useCallback } from 'react';
import { toast } from 'sonner';

import type { ContactAddress } from '@troia-v3/types';

import { Icon } from '@/shared/icons';
import { PrivacyText } from '@/shared/components/ui/PrivacyText';
import type { IconName } from '@/shared/icons';
import { formatPhoneNumber } from '@/shared/utils';

import { formatDateTime, formatShortDate } from '../detailsFormat';
import type { DetailsData } from '../useDetailsData';

interface ContactLayerProps {
  data: DetailsData;
  onEdit: () => void;
}

type CanalId = 'whatsapp' | 'phone' | 'email' | 'instagram';

interface DefinicaoDeCanal {
  id: CanalId;
  icone: IconName;
  /** Só para o `title` do valor — o ícone é quem identifica o canal na linha. */
  rotulo: string;
  /** Telefone ganha máscara de leitura; e-mail e Instagram vão como estão. */
  ehTelefone: boolean;
}

const CANAIS: DefinicaoDeCanal[] = [
  { id: 'whatsapp', icone: 'message-circle', rotulo: 'WhatsApp', ehTelefone: true },
  { id: 'phone', icone: 'phone', rotulo: 'Telefone', ehTelefone: true },
  { id: 'email', icone: 'mail', rotulo: 'E-mail', ehTelefone: false },
  { id: 'instagram', icone: 'instagram', rotulo: 'Instagram', ehTelefone: false },
];

interface CanalNaTela {
  id: CanalId;
  icone: IconName;
  rotulo: string;
  /** O que aparece (telefone com máscara). */
  exibicao: string;
  /** O que vai para a área de transferência: o valor como está gravado. */
  copia: string;
  /** Quantos outros valores o mesmo canal tem, além do primeiro. */
  extras: number;
}

// A cor tem de sair de token: a mesma linha é lida no tema claro e no escuro.
const ESTILO_VALOR: React.CSSProperties = {
  color: 'var(--theme-card-text)',
  fontSize: 13,
  lineHeight: '18px',
};

const ESTILO_DISCRETO: React.CSSProperties = {
  color: 'var(--theme-muted-text)',
  fontSize: 11,
  fontWeight: 600,
};

/** Vazio, só espaço em branco e `undefined` são a mesma coisa: "não temos". */
function limpar(valor: string | undefined): string | undefined {
  const texto = valor?.trim();
  return texto ? texto : undefined;
}

/**
 * Junta só o que existe. É o que impede a vírgula solta do "Rua X, " e o hífen
 * órfão do "— Blumenau/SC" quando falta um pedaço do endereço.
 */
function juntar(partes: (string | undefined)[], separador: string): string {
  return partes.filter((p): p is string => p !== undefined).join(separador);
}

/**
 * Os valores que existem de verdade, na ordem do cadastro. Descartar os brancos
 * ANTES de escolher o primeiro é o que impede o canal inteiro de sumir da tela
 * quando a primeira posição do array foi gravada vazia — e é o que faz o "+2"
 * contar só o que a pessoa pode mesmo pedir.
 */
function valoresUteis(valores: string[] | undefined): string[] {
  return (valores ?? []).map((valor) => valor.trim()).filter((valor) => valor.length > 0);
}

/**
 * WhatsApp é gravado como JID (`5547...@s.whatsapp.net`) e, em conta nova, como
 * LID (`1854...@lid`). Só interessa o que vem antes do `@`: sem este corte o LID
 * sai cru na linha, porque `formatPhoneNumber` devolve a string original quando
 * não consegue validar o número. Vale só para canal de telefone — em e-mail o
 * `@` é o próprio endereço.
 */
function semSufixoDeJid(valor: string): string {
  const corte = valor.indexOf('@');
  return corte === -1 ? valor : valor.slice(0, corte);
}

function montarEndereco(endereco: ContactAddress | undefined): string[] {
  if (!endereco) return [];

  const linhas: string[] = [];

  // Complemento na MESMA linha da rua, como no desenho aprovado: em painel
  // estreito, uma linha só para "sala 12" gasta altura sem informar nada.
  const ruaENumero = juntar([limpar(endereco.street), limpar(endereco.number)], ', ');
  const logradouro = juntar([ruaENumero || undefined, limpar(endereco.complement)], ' · ');
  if (logradouro) linhas.push(logradouro);

  const cidadeEUf = juntar([limpar(endereco.city), limpar(endereco.state)], '/');
  const bairroECidade = juntar([limpar(endereco.neighborhood), cidadeEUf || undefined], ' — ');
  if (bairroECidade) linhas.push(bairroECidade);

  const cep = limpar(endereco.zipCode);
  if (cep) linhas.push(`CEP ${cep}`);

  return linhas;
}

/**
 * Idade em anos completos. Devolve `undefined` quando a data não dá conta
 * (texto inválido, data no futuro, ano absurdo) — aí a tela mostra só a data,
 * sem o complemento, em vez de "· -3 anos".
 */
function idadeEmAnos(iso: string | undefined, agora: Date): number | undefined {
  if (!iso) return undefined;

  const nascimento = new Date(iso);
  if (Number.isNaN(nascimento.getTime())) return undefined;

  let anos = agora.getFullYear() - nascimento.getFullYear();
  const meses = agora.getMonth() - nascimento.getMonth();
  if (meses < 0 || (meses === 0 && agora.getDate() < nascimento.getDate())) anos -= 1;

  return anos >= 0 && anos <= 130 ? anos : undefined;
}

const ContactLayer: React.FC<ContactLayerProps> = ({ data, onEdit }) => {
  const contact = data.contact;

  const copiar = useCallback(async (valor: string, rotulo: string) => {
    try {
      await navigator.clipboard.writeText(valor);
      toast.success(`${rotulo} copiado para a área de transferência`);
    } catch {
      // Sem área de transferência (página em http, permissão negada): não é
      // erro que valha um aviso — o valor continua visível na tela para copiar
      // à mão.
    }
  }, []);

  if (!contact) {
    return (
      <div className="cdp-empty" data-testid="details-contact-loading">
        Carregando os dados do contato.
      </div>
    );
  }

  const identificadores = contact.identifiers;
  const canais: CanalNaTela[] = [];
  for (const canal of CANAIS) {
    const valores = valoresUteis(identificadores?.[canal.id]);
    // Índice de array é possivelmente `undefined` no modo estrito — daí o guarda.
    const primeiro = valores[0];
    if (primeiro === undefined) continue;

    // O que se copia é o valor gravado, não a máscara: "5547991236370" cola em
    // discador e em busca de outro sistema; "+55 47 99123-6370" não.
    const cru = canal.ehTelefone ? semSufixoDeJid(primeiro) : primeiro;
    canais.push({
      id: canal.id,
      icone: canal.icone,
      rotulo: canal.rotulo,
      exibicao: canal.ehTelefone ? formatPhoneNumber(cru) || cru : cru,
      copia: cru,
      extras: valores.length - 1,
    });
  }

  const nascimento = limpar(contact.personalInfo?.birthDate);
  const idade = idadeEmAnos(nascimento, new Date());
  const textoNascimento = `${formatShortDate(nascimento)}${
    idade === undefined ? '' : ` · ${idade} ${idade === 1 ? 'ano' : 'anos'}`
  }`;

  const documento = limpar(contact.personalInfo?.document);

  const linhasDoEndereco = montarEndereco(contact.personalInfo?.address);

  const semNada = canais.length === 0 && nascimento === undefined && documento === undefined && linhasDoEndereco.length === 0;

  return (
    <>
      {canais.length > 0 && (
        <>
          <div className="cdp-seclabel">Canais</div>
          <div data-testid="details-contact-channels">
            {canais.map((canal) => (
              <div className="cdp-line" key={canal.id} data-testid={`details-channel-${canal.id}`}>
                <span className="cdp-lineic">
                  <Icon name={canal.icone} className="w-4 h-4" />
                </span>
                {/* Modo privacidade: o mesmo telefone/e-mail já sai borrado no cabeçalho
                    do atendimento, na lista de conversas e na ficha do contato. Sem isto o
                    painel seria o único lugar que vaza o dado numa tela compartilhada.
                    O `title` é DESCARTADO pelo próprio PrivacyText quando o modo está
                    ligado — senão o valor cru voltaria no hover. */}
                <PrivacyText
                  className="flex-1 min-w-0 truncate"
                  style={ESTILO_VALOR}
                  title={canal.exibicao}
                >
                  {canal.exibicao}
                </PrivacyText>
                {canal.extras > 0 && (
                  <span
                    className="flex-none"
                    style={ESTILO_DISCRETO}
                    title={`${canal.rotulo}: mais ${canal.extras} cadastrado${canal.extras === 1 ? '' : 's'}`}
                  >
                    +{canal.extras}
                  </span>
                )}
                <button
                  type="button"
                  className="cdp-iconbtn"
                  title="Copiar"
                  data-testid={`details-copy-${canal.id}`}
                  onClick={() => {
                    void copiar(canal.copia, canal.rotulo);
                  }}
                >
                  <Icon name="copy" className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {(nascimento !== undefined || documento !== undefined) && (
        <>
          <div className="cdp-seclabel">Dados pessoais</div>
          {nascimento !== undefined && (
            <div className="cdp-kv" data-testid="details-contact-birth">
              <span className="cdp-k">Nascimento</span>
              <span className="cdp-v">{textoNascimento}</span>
            </div>
          )}
          {documento !== undefined && (
            <div className="cdp-kv" data-testid="details-contact-document">
              <span className="cdp-k">Documento</span>
              {/* Documento é dado pessoal: borra no modo privacidade, como os canais. */}
              <PrivacyText className="cdp-v">{documento}</PrivacyText>
            </div>
          )}
        </>
      )}

      {linhasDoEndereco.length > 0 && (
        <>
          <div className="cdp-seclabel">Endereço</div>
          {/* `flex-start` inline porque `.cdp-line` centraliza, e aqui o valor
              tem até três linhas: centralizado, o ícone flutuaria no meio. */}
          <div
            className="cdp-line"
            style={{ alignItems: 'flex-start' }}
            data-testid="details-contact-address"
          >
            <span className="cdp-lineic" style={{ marginTop: 1 }}>
              <Icon name="map-pin" className="w-4 h-4" />
            </span>
            <div className="flex-1 min-w-0">
              {linhasDoEndereco.map((linha, i) => (
                <PrivacyText as="div" key={`${i}-${linha}`} style={ESTILO_VALOR}>
                  {linha}
                </PrivacyText>
              ))}
            </div>
          </div>
        </>
      )}

      {semNada && (
        <div className="cdp-empty" data-testid="details-contact-empty">
          Nenhum dado cadastrado além do nome.
          <button
            type="button"
            className="cdp-btn"
            style={{ marginTop: 12 }}
            data-testid="details-edit-contact"
            onClick={onEdit}
          >
            <Icon name="edit" className="w-4 h-4" />
            Editar contato
          </button>
        </div>
      )}

      <div className="cdp-seclabel">Relacionamento</div>
      <div className="cdp-kv" data-testid="details-contact-since">
        <span className="cdp-k">Contato desde</span>
        <span className="cdp-v">{formatShortDate(contact.createdAt)}</span>
      </div>
      <div className="cdp-kv" data-testid="details-contact-last-interaction">
        <span className="cdp-k">Última interação</span>
        <span className="cdp-v">{formatDateTime(contact.lastInteractionAt)}</span>
      </div>
    </>
  );
};

export default ContactLayer;
